import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FameRecognitionServiceModule,
  type CrossScenarioRecognition,
} from '../services/host/FameRecognitionServiceModule';

/**
 * A8r Slice B (2026-06-04) — NPC Fame Recognition hook.
 *
 * For each newly-encountered TAROT NPC, fires a Fame Recognition roll
 * against Bond's fame_points. Successful rolls populate `recognizedIds`
 * which the ScenarioRunner composes into the dossier `recognizedNpcIds`
 * prop. NPCs whose roll fails appear in the dossier with name = ████████.
 *
 * Per prep brief D2/D4/D5:
 *   - Roll auto-fires on encountered-set change (no GM input needed)
 *   - Only TAROT NPCs require a roll (caller's `isTarotFn` gates it);
 *     non-TAROT auto-recognized in ScenarioRunner downstream
 *   - One roll per (npc, scenario) — backend UNIQUE constraint enforces
 *     idempotency; failed rolls don't re-fire even on retry
 *
 * Internal dual-ref pattern (R1.C4 fix from prep brief reviewer):
 *   - `rolledIdsRef` = every NPC for which a roll has fired (success OR
 *     failure). Used as the dedup gate BEFORE firing a new roll —
 *     prevents both redundant network calls AND infinite re-roll loops
 *     on recognized=false outcomes.
 *   - `recognizedIdsRef` = the recognized-true subset. This is what
 *     the hook EXPOSES as `recognizedIds`.
 *
 * React 18 setState async race avoidance (mirror useNpcEncounters /
 * useHeroPointSpend pattern): refs are read synchronously in the
 * encountered-set-change effect so a successful POST doesn't re-trigger
 * the effect via setState invalidation before the dedup gate updates.
 *
 * Consumer pattern (ScenarioRunner):
 *   const { recognizedIds } = useFameRecognition({
 *     campaignId, scenarioId, bondFamePoints, encounteredIds,
 *     isTarotFn: (id) => npcLookup.lookup(id)?.allegiance === 'tarot',
 *     npcFameFn: (id) => {
 *       const v = npcLookup.lookup(id)?.combat?.fame_points;
 *       return typeof v === 'number' ? v : 0;
 *     },
 *   });
 */
export interface UseFameRecognitionOptions {
  campaignId: string;
  scenarioId: string;
  /** Bond's accumulated fame_points (from pregen.combat.fame_points
   *  until character creation form ships). */
  bondFamePoints: number;
  /** The current encountered-NPC set from useNpcEncounters. Hook fires
   *  a roll for each new TAROT id in this set that hasn't already been
   *  rolled. */
  encounteredIds: ReadonlySet<string>;
  /** Predicate: is this NPC TAROT? Non-TAROT NPCs skip the roll entirely
   *  (handled downstream as auto-recognized). */
  isTarotFn: (npcId: string) => boolean;
  /** Per-NPC fame_points lookup. Caller MUST coerce string|number to
   *  number (NpcSheetData.combat is Record<string, string | number>);
   *  returns 0 when fame_points is missing on the NPC. */
  npcFameFn: (npcId: string) => number;
  /** Factory injected for tests. Defaults to constructing a real
   *  FameRecognitionServiceModule (which uses real fetch + localStorage). */
  serviceFactory?: () => FameRecognitionServiceModule;
  /** A8r Slice C (2026-06-04): callback fired when a NEW Fame Recognition
   *  roll completes in this session. NOT fired on registry-load mount
   *  (those are silent — replaying the cinematic for every loaded NPC
   *  would be a UX disaster). The `freshlyRolledRef` discipline gates
   *  this: only npcIds added to the set in THIS session's
   *  encountered-set-change effect fire the callback.
   *
   *  Consumers (ScenarioRunner) use this to set `pendingFameReveal`
   *  state and mount the FameRecognitionCinematic component. */
  onRollFired?: (npcId: string, recognized: boolean) => void;
  /** A8r Slice C5 (2026-06-04): callback fired when setOverride successfully
   *  APPLIES a non-null GM override (Force Yes or Force No). Mirrors
   *  onRollFired for natural d100 rolls — both feed the same cinematic
   *  state slot in ScenarioRunner.
   *
   *  Gates (handled inside setOverride):
   *    - fire only on POST success (no fire on POST failure / revert)
   *    - fire only when nextRecognized !== null (no fire on clear-override)
   *    - fire only when result !== null (no fire on 204 No Content)
   *    - fire with SERVER-AUTHORITATIVE `result.recognized` value, NOT the
   *      requested `nextRecognized`. The cinematic shows what the user will
   *      see in the dossier, which is the post-reconcile truth state.
   *
   *  Clearing an override (setOverride(npcId, null)) NEVER fires this —
   *  there's no new reveal: the raw natural-roll outcome was already shown
   *  by the original onRollFired when the roll happened. */
  onOverrideFired?: (npcId: string, recognized: boolean) => void;
}

export interface UseFameRecognitionResult {
  recognizedIds: ReadonlySet<string>;
  /** A8r Slice D: subset whose gm_override is currently NOT NULL. The
   *  ManualRecognitionBadge component renders next to NPC names in this
   *  set when the atmosphere flag is on. */
  manuallyOverriddenIds: ReadonlySet<string>;
  /** A8r Slice D: per-NPC ever_recognized aggregate across all scenarios
   *  in the campaign. Consumers compose this with `recognizedIds` to
   *  produce the dossier-visible set (cross-scenario carry preserves
   *  recognition narrative even when this scenario has no roll yet). */
  crossScenarioRecognizedIds: ReadonlySet<string>;
  /** A8r C2 (2026-06-04): per-NPC full cross-scenario metadata, keyed by
   *  npc_id. Each entry carries ever_recognized + first_recognized_at +
   *  first_scenario_id + override_count. The crossScenarioRecognizedIds
   *  Set above is derived from this Map (npc_ids where ever_recognized
   *  is true). Drives the Prior Encounters panel in HistoryPanel. */
  crossScenarioRecognitions: ReadonlyMap<string, CrossScenarioRecognition>;
  isLoading: boolean;
  error: string | null;
  /** A8r Slice C/D: GM force-recognize override. Optimistically updates
   *  recognizedIds + manuallyOverriddenIds sets, POSTs to /fame/override,
   *  reverts on error. Slice D extends to accept `null` to CLEAR the
   *  override (restore raw natural roll outcome).
   *
   *  The 204 No Content path (null + no prior row) resolves to null and
   *  is a no-op in the hook's state.
   *
   *  C3 (2026-06-04): opts.silent suppresses the onOverrideFired callback
   *  even on apply. Batch UI passes silent:true so a 6-NPC bulk override
   *  doesn't queue 6 cinematics. State reconcile is unaffected. */
  setOverride: (
    npcId: string,
    recognized: boolean | null,
    opts?: { silent?: boolean },
  ) => Promise<void>;
}

export function useFameRecognition({
  campaignId,
  scenarioId,
  bondFamePoints,
  encounteredIds,
  isTarotFn,
  npcFameFn,
  serviceFactory,
  onRollFired,
  onOverrideFired,
}: UseFameRecognitionOptions): UseFameRecognitionResult {
  const [recognizedIds, setRecognizedIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  // A8r Slice D: manually-overridden subset (gm_override !== null at last
  // read time). Drives the ManualRecognitionBadge UI.
  const [manuallyOverriddenIds, setManuallyOverriddenIds] = useState<
    ReadonlySet<string>
  >(() => new Set<string>());
  // A8r Slice D: cross-scenario carry set (ever_recognized across all
  // scenarios in campaign). Fetched once on init + on campaign change.
  const [crossScenarioRecognizedIds, setCrossScenarioRecognizedIds] = useState<
    ReadonlySet<string>
  >(() => new Set<string>());
  // A8r C2 (2026-06-04): full per-NPC cross-scenario metadata Map. The
  // Set above is derived from this Map at update time (npc_ids where
  // ever_recognized=true). HistoryPanel's Prior Encounters section reads
  // the Map directly to show scenario_id + first_recognized date.
  const [crossScenarioRecognitions, setCrossScenarioRecognitions] = useState<
    ReadonlyMap<string, CrossScenarioRecognition>
  >(() => new Map());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // R1.C4 dual-ref pattern. recognizedIdsRef is the OUTPUT set;
  // rolledIdsRef is the DEDUP gate (includes recognized=false rows so
  // we don't re-roll a failed recognition forever).
  const rolledIdsRef = useRef<Set<string>>(new Set());
  const recognizedIdsRef = useRef<Set<string>>(new Set());
  // A8r Slice D: sister ref for manuallyOverriddenIds (mirrors R2.C1
  // concurrent-safe revert pattern — snapshot at setOverride entry).
  const manuallyOverriddenIdsRef = useRef<Set<string>>(new Set());

  // A8r Slice C (2026-06-04): freshlyRolledRef tracks npcIds whose roll
  // was fired by THIS session (NOT by registry-load mount). Used to gate
  // the onRollFired callback so the cinematic doesn't replay for every
  // pre-existing recognition when the SPA reloads.
  //
  // Cleared on scenario change (init effect resets to new Set()) so
  // switching scenarios doesn't carry over "fresh" state.
  const freshlyRolledRef = useRef<Set<string>>(new Set());

  // Stable callback ref so the effect dep doesn't tear up on every
  // render of the parent.
  const onRollFiredRef = useRef<typeof onRollFired>(onRollFired);
  onRollFiredRef.current = onRollFired;
  // A8r Slice C5: sister ref for the GM-override cinematic trigger.
  // Same stable-ref pattern — caller can pass an inline arrow without
  // tearing the setOverride effect dep on every parent re-render.
  const onOverrideFiredRef = useRef<typeof onOverrideFired>(onOverrideFired);
  onOverrideFiredRef.current = onOverrideFired;

  // The service instance is stable across renders so tests can spy on
  // method calls. Created lazily via the factory (mirrors useNpcEncounters).
  const serviceRef = useRef<FameRecognitionServiceModule | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = serviceFactory
      ? serviceFactory()
      : new FameRecognitionServiceModule();
  }

  // Initial sync (or re-sync on campaign/scenario change).
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    // A8r Slice C: scenario change clears freshlyRolledRef — new scenario
    // starts with no fresh rolls in flight.
    freshlyRolledRef.current = new Set();
    const service = serviceRef.current;
    if (service === null) {
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      try {
        const registry = await service.getRegistry(campaignId);
        if (cancelled) return;
        // Scope the in-memory sets to recognitions from THIS scenario.
        // A8r Slice C: effective recognized = gm_override ?? recognized.
        // The backend's _row_to_entry already sets `entry.recognized` to the
        // effective value, but defensive: read both fields so the frontend
        // contract is robust against future schema/API changes.
        // A8r Slice D: also populate manuallyOverriddenIds from gm_override
        // !== null at registry-load time.
        const rolled = new Set<string>();
        const recognized = new Set<string>();
        const manuallyOverridden = new Set<string>();
        for (const entry of registry.recognitions) {
          if (entry.scenario_id !== scenarioId) continue;
          rolled.add(entry.npc_id);
          const effective =
            entry.gm_override == null ? entry.recognized : entry.gm_override;
          if (effective) recognized.add(entry.npc_id);
          if (entry.gm_override !== null && entry.gm_override !== undefined) {
            manuallyOverridden.add(entry.npc_id);
          }
        }
        rolledIdsRef.current = rolled;
        recognizedIdsRef.current = recognized;
        manuallyOverriddenIdsRef.current = manuallyOverridden;
        setRecognizedIds(recognized);
        setManuallyOverriddenIds(manuallyOverridden);

        // A8r Slice D: parallel fetch of cross-scenario aggregate. Failure
        // is silent — hook still functional with per-scenario state only.
        // C2 (2026-06-04): also populate the full-metadata Map.
        try {
          const cross = await (
            service as unknown as {
              getCrossScenario?: (cid: string) => Promise<{
                recognitions: CrossScenarioRecognition[];
              }>;
            }
          ).getCrossScenario?.(campaignId);
          if (!cancelled && cross) {
            const crossMap = new Map<string, CrossScenarioRecognition>();
            const crossSet = new Set<string>();
            for (const c of cross.recognitions) {
              crossMap.set(c.npc_id, c);
              if (c.ever_recognized) crossSet.add(c.npc_id);
            }
            setCrossScenarioRecognitions(crossMap);
            setCrossScenarioRecognizedIds(crossSet);
          }
        } catch {
          // Silent: cross-scenario is an enhancement, not load-blocking.
        }

        setIsLoading(false);
      } catch (exc) {
        if (cancelled) return;
        const message = exc instanceof Error ? exc.message : String(exc);
        setError(message);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, scenarioId]);

  // Auto-fire rolls when encountered set changes — for each new TAROT
  // NPC not already in rolledIdsRef.
  useEffect(() => {
    const candidates: string[] = [];
    for (const id of encounteredIds) {
      if (rolledIdsRef.current.has(id)) continue;
      if (!isTarotFn(id)) continue;
      candidates.push(id);
    }
    if (candidates.length === 0) return;

    let cancelled = false;
    const service = serviceRef.current;
    if (service === null) return;

    void (async () => {
      await Promise.all(
        candidates.map(async (npcId) => {
          try {
            const npcFame = npcFameFn(npcId);
            const entry = await service.rollRecognition(
              campaignId,
              npcId,
              scenarioId,
              bondFamePoints,
              npcFame,
            );
            if (cancelled) return;
            // Mark as rolled REGARDLESS of outcome (dedup gate).
            const nextRolled = new Set(rolledIdsRef.current);
            nextRolled.add(npcId);
            rolledIdsRef.current = nextRolled;
            // A8r Slice C: mark as freshly rolled (THIS session) so the
            // cinematic fires via onRollFired callback below.
            const nextFresh = new Set(freshlyRolledRef.current);
            nextFresh.add(npcId);
            freshlyRolledRef.current = nextFresh;
            // Compute effective recognized — server's _row_to_entry already
            // applied gm_override, so entry.recognized is effective.
            const effective =
              entry.gm_override == null ? entry.recognized : entry.gm_override;
            if (effective) {
              const nextRecognized = new Set(recognizedIdsRef.current);
              nextRecognized.add(npcId);
              recognizedIdsRef.current = nextRecognized;
              setRecognizedIds(nextRecognized);
            }
            // A8r Slice C: fire onRollFired callback for the cinematic.
            // Only after the dedup + recognized state updates so the
            // consumer sees consistent post-roll state.
            const cb = onRollFiredRef.current;
            if (cb !== undefined) {
              cb(npcId, effective);
            }
          } catch (exc) {
            if (cancelled) return;
            // Best-effort: rolledIdsRef stays as it was; next encountered-
            // set change re-tries. Distinct from successful recognized=false
            // (which DOES update rolledIdsRef so we don't re-roll forever).
            const message = exc instanceof Error ? exc.message : String(exc);
            setError(message);
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
    // The effect intentionally does NOT include the refs in deps — we
    // read them synchronously. encounteredIds + the callbacks are the
    // canonical change signals. isTarotFn + npcFameFn are stable when
    // wrapped in useCallback by the consumer (ScenarioRunner does this).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounteredIds, campaignId, scenarioId, bondFamePoints, isTarotFn, npcFameFn]);

  // A8r Slice C/D: GM force-recognize override.
  // Slice D extends to 3-state: `nextRecognized` is `boolean | null`.
  //   - true:  force recognized
  //   - false: force unknown
  //   - null:  clear the override (restore natural roll outcome)
  //
  // Optimistically updates BOTH recognizedIds + manuallyOverriddenIds before
  // the POST; reverts both on error using the priorSet snapshot pattern
  // extended to cover BOTH refs (per R2.C1 → 3-state extension).
  const setOverride = useCallback(
    async (
      npcId: string,
      nextRecognized: boolean | null,
      opts?: { silent?: boolean },
    ): Promise<void> => {
      const service = serviceRef.current;
      if (service === null) {
        throw new Error('useFameRecognition: service not initialized');
      }
      const silent = opts?.silent === true;
      // R2.C1 fix (extended for Slice D 3-state): snapshot BOTH priors.
      const priorRecognized = recognizedIdsRef.current;
      const priorManuallyOverridden = manuallyOverriddenIdsRef.current;
      const wasRecognized = priorRecognized.has(npcId);
      const wasManuallyOverridden = priorManuallyOverridden.has(npcId);

      // Optimistic update — fresh Sets so we don't mutate the snapshots.
      const nextRecSet = new Set(priorRecognized);
      const nextManSet = new Set(priorManuallyOverridden);

      if (nextRecognized === null) {
        // Clear: remove from manually-overridden; the effective recognized
        // state will resolve from the natural roll after the server
        // response comes back. Optimistically, we DON'T know the raw value
        // without a re-fetch, so we leave recognizedIds as-is until the
        // server response lands. (Test 'Slice D: setOverride(null) clears
        // manuallyOverriddenIds + reverts recognizedIds to raw' confirms
        // the post-response state.)
        nextManSet.delete(npcId);
      } else {
        // Force true/false: optimistic recognized = nextRecognized + add
        // to manually-overridden.
        if (nextRecognized) nextRecSet.add(npcId);
        else nextRecSet.delete(npcId);
        nextManSet.add(npcId);
      }

      recognizedIdsRef.current = nextRecSet;
      manuallyOverriddenIdsRef.current = nextManSet;
      setRecognizedIds(nextRecSet);
      setManuallyOverriddenIds(nextManSet);

      try {
        const result = await (
          service as unknown as {
            setOverride: (
              cid: string,
              nid: string,
              sid: string,
              rec: boolean | null,
            ) => Promise<{
              recognized: boolean;
              gm_override?: boolean | null;
            } | null>;
          }
        ).setOverride(campaignId, npcId, scenarioId, nextRecognized);

        // Reconcile with the authoritative server response.
        if (result !== null) {
          const effectiveRecognized = result.recognized;
          const isManual =
            result.gm_override !== null && result.gm_override !== undefined;
          const reconciledRec = new Set(recognizedIdsRef.current);
          const reconciledMan = new Set(manuallyOverriddenIdsRef.current);
          if (effectiveRecognized) reconciledRec.add(npcId);
          else reconciledRec.delete(npcId);
          if (isManual) reconciledMan.add(npcId);
          else reconciledMan.delete(npcId);
          recognizedIdsRef.current = reconciledRec;
          manuallyOverriddenIdsRef.current = reconciledMan;
          setRecognizedIds(reconciledRec);
          setManuallyOverriddenIds(reconciledMan);

          // A8r Slice C5 (2026-06-04): fire the cinematic-trigger callback
          // ONLY on apply (nextRecognized !== null). Clearing the override
          // has no NEW reveal — the raw natural-roll outcome already fired
          // its own onRollFired/cinematic. Server-authoritative value
          // wins so the cinematic shows post-reconcile truth.
          //
          // C3 (2026-06-04): the silent opt suppresses the callback even
          // on apply — batch UI uses this so a 6-NPC bulk update doesn't
          // queue 6 cinematics.
          if (nextRecognized !== null && !silent) {
            const cb = onOverrideFiredRef.current;
            if (cb) cb(npcId, effectiveRecognized);
          }
        }
        // null result (HTTP 204 from null-on-missing-row): leave optimistic
        // state in place. nextRecSet/nextManSet already reflect the no-op
        // shape (manuallyOverridden removed, recognized untouched).
        // C5: NEVER fires onOverrideFired on 204 — no server-side change.
      } catch (exc) {
        // R2.C1 + Slice D: revert from BOTH priorSet snapshots, merging
        // concurrent modifications from the dirty refs.
        const dirtyRec = recognizedIdsRef.current;
        const dirtyMan = manuallyOverriddenIdsRef.current;

        const revertedRec = new Set(priorRecognized);
        const revertedMan = new Set(priorManuallyOverridden);

        // Apply only this call's correction to its own npcId.
        if (wasRecognized) revertedRec.add(npcId);
        else revertedRec.delete(npcId);
        if (wasManuallyOverridden) revertedMan.add(npcId);
        else revertedMan.delete(npcId);

        // Merge concurrent modifications on OTHER npcIds back in.
        for (const id of dirtyRec) {
          if (id === npcId) continue;
          if (!priorRecognized.has(id)) revertedRec.add(id);
        }
        for (const id of priorRecognized) {
          if (id === npcId) continue;
          if (!dirtyRec.has(id)) revertedRec.delete(id);
        }
        for (const id of dirtyMan) {
          if (id === npcId) continue;
          if (!priorManuallyOverridden.has(id)) revertedMan.add(id);
        }
        for (const id of priorManuallyOverridden) {
          if (id === npcId) continue;
          if (!dirtyMan.has(id)) revertedMan.delete(id);
        }

        recognizedIdsRef.current = revertedRec;
        manuallyOverriddenIdsRef.current = revertedMan;
        setRecognizedIds(revertedRec);
        setManuallyOverriddenIds(revertedMan);
        const message = exc instanceof Error ? exc.message : String(exc);
        setError(message);
        throw exc;
      }
    },
    [campaignId, scenarioId],
  );

  return {
    recognizedIds,
    manuallyOverriddenIds,
    crossScenarioRecognizedIds,
    crossScenarioRecognitions,
    isLoading,
    error,
    setOverride,
  };
}
